import { Id, toast } from 'react-toastify';
import { showDownloadsModal } from 'redux/mode/actions';
import store from 'redux/store';
import { exportDownloadUrl } from 'utils';
import * as S from './styles';

interface ReadyResult {
	exportId: string;
	filename: string;
}

// Content rendered inside the react-toastify toast when an export finishes.
// The anchor needs the `download` attribute so the browser treats the click
// as a fresh user gesture (the whole reason this flow exists — Chrome
// silently blocks the second of multiple auto-triggered downloads).
const ExportReadyToast = ({ exportId, filename, toastId }: ReadyResult & { toastId: Id }) => {
	const href = exportDownloadUrl(exportId);
	return (
		<S.Base>
			<S.Message>Your download is ready.</S.Message>
			<S.Links>
				<S.LinkAnchor
					href={href}
					download={filename}
					onClick={(e) => {
						e.stopPropagation();
						toast.dismiss(toastId);
					}}
				>
					Download
				</S.LinkAnchor>
				<S.LinkButton
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						store.dispatch(showDownloadsModal());
						toast.dismiss(toastId);
					}}
				>
					Open Downloads
				</S.LinkButton>
			</S.Links>
		</S.Base>
	);
};

interface RunExportArgs<T extends ReadyResult> {
	pendingLabel: string;
	errorLabel: string;
	run: () => Promise<T>;
}

// Wraps an export with a single react-toastify toast that lifecycles from
// spinner → ready CTA (or error) in place via toast.loading + toast.update.
// Callers return { exportId, filename }; on success the toast content becomes
// <ExportReadyToast>, giving the user two explicit links plus the default
// close X (closeButton: true) so they can dismiss without acting.
export async function runExportWithToast<T extends ReadyResult>({
	pendingLabel,
	errorLabel,
	run,
}: RunExportArgs<T>): Promise<T> {
	const toastId = toast.loading(pendingLabel);
	try {
		const result = await run();
		toast.update(toastId, {
			render: (
				<ExportReadyToast exportId={result.exportId} filename={result.filename} toastId={toastId} />
			),
			type: 'success',
			isLoading: false,
			autoClose: false,
			closeOnClick: false,
			closeButton: true,
		});
		return result;
	} catch (e) {
		toast.update(toastId, {
			render: errorLabel,
			type: 'error',
			isLoading: false,
			autoClose: 5000,
			closeOnClick: true,
		});
		throw e;
	}
}

export default ExportReadyToast;
